import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { supabase } from '../../src/lib/supabase';
import { Pipeline, PipelineStage } from '../../types';
import { Plus, Trash2, Edit2, Check, X, Move, ChevronDown, ChevronRight, Columns } from 'lucide-react';
import { DragDropContext, Draggable, DropResult } from '@hello-pangea/dnd';
import { StrictModeDroppable } from '../../src/components/StrictModeDroppable';
import { useAuth } from '../../src/contexts/AuthContext';
import { logUsageEvent } from '../../src/lib/logger';

const PipelineSettings = () => {
    const { user } = useAuth();
    const [pipelines, setPipelines] = useState<Pipeline[]>([]);
    const [selectedPipeline, setSelectedPipeline] = useState<string | null>(null);
    const [stages, setStages] = useState<PipelineStage[]>([]);
    const [loading, setLoading] = useState(true);

    const [editingPipelineId, setEditingPipelineId] = useState<string | null>(null);
    const [editingPipelineName, setEditingPipelineName] = useState('');

    const [newPipelineName, setNewPipelineName] = useState('');
    const [showNewPipelineInput, setShowNewPipelineInput] = useState(false);

    // Stage editing
    const [newStageName, setNewStageName] = useState('');
    const [editingStageId, setEditingStageId] = useState<string | null>(null);
    const [editingStageName, setEditingStageName] = useState('');

    useEffect(() => {
        fetchPipelines();
    }, []);

    useEffect(() => {
        if (selectedPipeline) {
            fetchStages(selectedPipeline);
        } else {
            setStages([]);
        }
    }, [selectedPipeline]);

    const fetchPipelines = async () => {
        setLoading(true);
        const { data, error } = await supabase
            .from('pipelines')
            .select('*')
            .order('created_at', { ascending: true });

        if (data) {
            setPipelines(data);
            if (data.length > 0 && !selectedPipeline) {
                setSelectedPipeline(data[0].id);
            }
        }
        setLoading(false);
    };

    const fetchStages = async (pipelineId: string) => {
        const { data } = await supabase
            .from('pipeline_stages')
            .select('*')
            .eq('pipeline_id', pipelineId)
            .order('position', { ascending: true });

        if (data) setStages(data);
    };

    const handleCreatePipeline = async () => {
        if (!newPipelineName.trim()) return;

        const { data, error } = await supabase
            .from('pipelines')
            .insert({ name: newPipelineName })
            .select()
            .single();

        if (error) {
            console.error('Error creating pipeline:', error);
            alert(`Erro ao criar funil: ${error.message}`);
            return;
        }

        if (data) {
            setPipelines([...pipelines, data]);
            setNewPipelineName('');
            setShowNewPipelineInput(false);
            setSelectedPipeline(data.id);
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'pipelines', acao: 'criar_pipeline', origem: 'acao' });
        }
    };

    const handleUpdatePipeline = async (id: string) => {
        if (!editingPipelineName.trim()) return;

        const { error } = await supabase
            .from('pipelines')
            .update({ name: editingPipelineName })
            .eq('id', id);

        if (!error) {
            setPipelines(pipelines.map(p => p.id === id ? { ...p, name: editingPipelineName } : p));
            setEditingPipelineId(null);
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'pipelines', acao: 'editar_pipeline', origem: 'acao' });
        }
    };

    const handleDeletePipeline = async (id: string) => {
        if (!confirm('Tem certeza? Todas as negociações neste funil serão perdidas ou precisarão ser migradas.')) return;

        const { error } = await supabase
            .from('pipelines')
            .delete()
            .eq('id', id);

        if (!error) {
            const newPipelines = pipelines.filter(p => p.id !== id);
            setPipelines(newPipelines);
            if (selectedPipeline === id) {
                setSelectedPipeline(newPipelines.length > 0 ? newPipelines[0].id : null);
            }
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'pipelines', acao: 'excluir_pipeline', origem: 'acao' });
        }
    };

    const handleAddStage = async () => {
        if (!selectedPipeline || !newStageName.trim()) return;

        const position = stages.length;

        const { data, error } = await supabase
            .from('pipeline_stages')
            .insert({
                pipeline_id: selectedPipeline,
                name: newStageName,
                position
            })
            .select()
            .single();

        if (data) {
            setStages([...stages, data]);
            setNewStageName('');
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'pipelines', acao: 'criar_etapa_pipeline', origem: 'acao' });
        }
    };

    const handleUpdateStage = async (id: string) => {
        if (!editingStageName.trim()) return;

        // 1. Get current stage name before update to match against negotiations
        const currentStage = stages.find(s => s.id === id);
        if (!currentStage) return;
        const oldName = currentStage.name;

        // 2. Update the Stage Name in pipeline_stages
        const { error } = await supabase
            .from('pipeline_stages')
            .update({ name: editingStageName })
            .eq('id', id);

        if (!error) {
            // 3. Update local state
            setStages(stages.map(s => s.id === id ? { ...s, name: editingStageName } : s));
            setEditingStageId(null);
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'pipelines', acao: 'editar_etapa_pipeline', origem: 'acao' });

            // 4. Update all negotiations that are in this pipeline and have the OLD stage name
            // Note: Use RPC or raw SQL if possible for atomicity, but simple update works for now.
            if (selectedPipeline && oldName !== editingStageName) {
                const { error: negError } = await supabase
                    .from('negociacoes')
                    .update({ stage: editingStageName })
                    .eq('pipeline_id', selectedPipeline)
                    .eq('stage', oldName);

                if (negError) {
                    console.error('Error migrating negotiations to new stage name:', negError);
                    alert('Etapa renomeada, mas houve erro ao atualizar as negociações. Contate o suporte.');
                }
            }
        }
    };

    const handleDeleteStage = async (id: string) => {
        if (!confirm('Excluir esta etapa?')) return;

        const { error } = await supabase
            .from('pipeline_stages')
            .delete()
            .eq('id', id);

        if (!error) {
            setStages(stages.filter(s => s.id !== id));
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'pipelines', acao: 'excluir_etapa_pipeline', origem: 'acao' });
        }
    };

    const handleDragEnd = async (result: DropResult) => {
        if (!result.destination) return;

        const start = result.source.index;
        const end = result.destination.index;

        if (start === end) return;

        const newStages = Array.from(stages);
        const [reorderedItem] = newStages.splice(start, 1);
        newStages.splice(end, 0, reorderedItem);

        // Optimistic update
        setStages(newStages);

        // Update database
        // We need to update the position of all affected items
        // A simple strategy is to update the position of the moved item and any items between start and end
        // Or just update all items with their new index as position
        const updates = newStages.map((stage, index) => ({
            id: stage.id,
            position: index,
            name: stage.name, // Required by upsert if not partial, but update is better
            pipeline_id: selectedPipeline,
            color: stage.color // Maintain other fields
        }));

        // We can do a bulk upsert if we include all necessary fields, or multiple updates.
        // Supabase upsert is efficient.
        const { error } = await supabase
            .from('pipeline_stages')
            .upsert(updates, { onConflict: 'id' });

        if (error) {
            console.error('Error reordering stages:', error);
            // Revert state if needed, or just show error
            alert('Erro ao salvar a nova ordem das etapas.');
            fetchStages(selectedPipeline!); // Refresh from server to sync
        } else {
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'pipelines', acao: 'reordenar_etapas_pipeline', origem: 'acao' });
        }
    };

    return (
        <DragDropContext onDragEnd={handleDragEnd}>
            <div className="flex flex-col gap-6">
                <div>
                    <h2 className="text-xl font-bold font-poppins text-gray-900 dark:text-white">Configuração de Funis</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Gerencie seus funis de vendas e etapas.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Pipelines List */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
                        <div className="p-4 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
                            <h3 className="font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-2">
                                <Columns size={18} />
                                Funis
                            </h3>
                            <button
                                onClick={() => setShowNewPipelineInput(true)}
                                className="p-1 hover:bg-white dark:hover:bg-gray-600 rounded-md transition-colors text-primary"
                            >
                                <Plus size={18} />
                            </button>
                        </div>

                        <div className="divide-y divide-gray-100 dark:divide-gray-700">
                            {showNewPipelineInput && (
                                <div className="p-3 flex gap-2 animate-fade-in bg-blue-50/50">
                                    <input
                                        autoFocus
                                        type="text"
                                        className="flex-1 text-sm border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                                        placeholder="Nome do Funil"
                                        value={newPipelineName}
                                        onChange={e => setNewPipelineName(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && handleCreatePipeline()}
                                    />
                                    <button onClick={handleCreatePipeline} className="text-green-600 hover:bg-green-50 p-1 rounded">
                                        <Check size={16} />
                                    </button>
                                    <button onClick={() => setShowNewPipelineInput(false)} className="text-red-500 hover:bg-red-50 p-1 rounded">
                                        <X size={16} />
                                    </button>
                                </div>
                            )}

                            {pipelines.map(pipeline => (
                                <div
                                    key={pipeline.id}
                                    className={`p-3 flex items-center justify-between cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50 border-l-4 group ${selectedPipeline === pipeline.id ? 'bg-primary/5 dark:bg-primary/10 border-l-primary' : 'border-l-transparent'}`}
                                    onClick={() => setSelectedPipeline(pipeline.id)}
                                >
                                    {editingPipelineId === pipeline.id ? (
                                        <div className="flex gap-2 w-full items-center" onClick={e => e.stopPropagation()}>
                                            <input
                                                type="text"
                                                className="flex-1 text-sm p-1 border rounded"
                                                value={editingPipelineName}
                                                onChange={e => setEditingPipelineName(e.target.value)}
                                                autoFocus
                                            />
                                            <button onClick={() => handleUpdatePipeline(pipeline.id)} className="text-green-600"><Check size={14} /></button>
                                            <button onClick={() => setEditingPipelineId(null)} className="text-red-500"><X size={14} /></button>
                                        </div>
                                    ) : (
                                        <>
                                            <span className={`text-sm font-medium ${selectedPipeline === pipeline.id ? 'text-primary' : 'text-gray-700 dark:text-gray-300'}`}>
                                                {pipeline.name}
                                            </span>
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setEditingPipelineId(pipeline.id);
                                                        setEditingPipelineName(pipeline.name);
                                                    }}
                                                    className="p-1 text-gray-300 hover:text-blue-500 transition-colors"
                                                >
                                                    <Edit2 size={14} />
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeletePipeline(pipeline.id);
                                                    }}
                                                    className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Stages List */}
                    <div className="md:col-span-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col h-full transition-colors">
                        <div className="p-4 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-100 dark:border-gray-700 mb-4">
                            <h3 className="font-semibold text-gray-700 dark:text-gray-200">Etapas do Funil</h3>
                            {selectedPipeline && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Gerencie as etapas para "{pipelines.find(p => p.id === selectedPipeline)?.name}"</p>}
                        </div>

                        <div className="flex-1 p-4 overflow-y-auto">
                            {!selectedPipeline ? (
                                <div className="flex flex-col items-center justify-center h-40 text-gray-400">
                                    <Columns size={32} className="mb-2 opacity-50" />
                                    <p>Selecione um funil para ver suas etapas</p>
                                </div>
                            ) : (

                                <>
                                    <StrictModeDroppable droppableId="stages-list">
                                        {(provided) => (
                                            <div
                                                className="flex flex-col gap-3"
                                                {...provided.droppableProps}
                                                ref={provided.innerRef}
                                            >
                                                {stages.map((stage, index) => (
                                                    <Draggable key={stage.id} draggableId={stage.id} index={index}>
                                                        {(provided, snapshot) => {
                                                            const child = (
                                                                <div
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    className={`flex items-center gap-3 bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm transition-colors hover:border-primary/30 ${snapshot.isDragging ? 'opacity-50 ring-2 ring-primary' : ''}`}
                                                                    style={{
                                                                        ...provided.draggableProps.style,
                                                                        left: snapshot.isDragging && provided.draggableProps.style && 'left' in provided.draggableProps.style ? (provided.draggableProps.style as any).left : undefined,
                                                                        top: snapshot.isDragging && provided.draggableProps.style && 'top' in provided.draggableProps.style ? (provided.draggableProps.style as any).top : undefined,
                                                                    }}
                                                                >
                                                                    <div
                                                                        {...provided.dragHandleProps}
                                                                        className="text-gray-300 cursor-move hover:text-gray-500 dark:hover:text-gray-400"
                                                                    >
                                                                        <Move size={14} />
                                                                    </div>
                                                                    <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xs text-gray-500 dark:text-gray-300 font-mono">
                                                                        {index + 1}
                                                                    </div>

                                                                    {editingStageId === stage.id ? (
                                                                        <div className="flex-1 flex items-center gap-2">
                                                                            <input
                                                                                type="text"
                                                                                value={editingStageName}
                                                                                onChange={(e) => setEditingStageName(e.target.value)}
                                                                                className="flex-1 text-sm p-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-primary focus:border-primary outline-none dark:bg-gray-700 dark:text-white"
                                                                                autoFocus
                                                                                onKeyDown={e => e.key === 'Enter' && handleUpdateStage(stage.id)}
                                                                            />
                                                                            <button onClick={() => handleUpdateStage(stage.id)} className="text-green-600 p-1 hover:bg-green-50 dark:hover:bg-green-900/30 rounded">
                                                                                <Check size={14} />
                                                                            </button>
                                                                            <button onClick={() => setEditingStageId(null)} className="text-red-500 p-1 hover:bg-red-50 dark:hover:bg-red-900/30 rounded">
                                                                                <X size={14} />
                                                                            </button>
                                                                        </div>
                                                                    ) : (
                                                                        <>
                                                                            <span className="flex-1 text-sm font-medium text-gray-700 dark:text-gray-300">{stage.name}</span>
                                                                            <div className="flex items-center gap-1">
                                                                                <button
                                                                                    onClick={() => {
                                                                                        setEditingStageId(stage.id);
                                                                                        setEditingStageName(stage.name);
                                                                                    }}
                                                                                    className="p-1.5 text-gray-300 hover:text-blue-500 rounded transition-colors"
                                                                                    title="Renomear etapa"
                                                                                >
                                                                                    <Edit2 size={14} />
                                                                                </button>
                                                                                <button
                                                                                    onClick={() => handleDeleteStage(stage.id)}
                                                                                    className="p-1.5 text-gray-300 hover:text-red-600 rounded transition-colors"
                                                                                    title="Excluir etapa"
                                                                                >
                                                                                    <Trash2 size={14} />
                                                                                </button>
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            );

                                                            if (snapshot.isDragging) {
                                                                return ReactDOM.createPortal(child, document.body);
                                                            }

                                                            return child;
                                                        }}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </StrictModeDroppable>

                                    <div className="flex items-center gap-2 mt-4 pt-4 border-t border-dashed border-gray-200 dark:border-gray-700">
                                        <input
                                            type="text"
                                            placeholder="Nome da nova etapa..."
                                            className="flex-1 text-sm border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-lg focus:bg-white dark:focus:bg-gray-800 focus:ring-primary focus:border-primary transition-colors text-gray-900 dark:text-white"
                                            value={newStageName}
                                            onChange={e => setNewStageName(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && handleAddStage()}
                                        />
                                        <button
                                            onClick={handleAddStage}
                                            className="bg-gray-900 dark:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary transition-colors flex items-center gap-2"
                                        >
                                            <Plus size={16} />
                                            Adicionar
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div >
        </DragDropContext>
    );
};

export default PipelineSettings;
